import {
  DetailBooleanField,
  DetailCommentBlock,
  DetailField,
  DetailSection,
  RendererQueryState
} from '../common'

import { trimToNull } from 'utils/helpers/stringUtils'
import { UploadedVisualsSection } from '../common/UploadedVisualsSection.component'
import { AssignmentAddonsSection } from './shared/AssignmentAddonsSection.component'
import { AssignmentRendererProps } from './AssignmentRenderer.types'
import { FC, useEffect } from 'react'
import { FloorPlanConfigSection } from './shared/FloorPlanConfigSection.component'
import { isNotFoundError } from 'utils/helpers/queryErrorHelpers'
import { logger } from 'utils/logging'
import { useAssignmentDetailDrawerWorkspaceId } from '../AssignmentDetailDrawer.context'
import { useGetAssignmentFloorPlanDetails } from 'dataQueries/assignmentDetails.query'
import { useTranslation } from 'react-i18next'

/**
 * Assignment detail renderer for floor plan products.
 * Handles FLOOR_PLAN_EDITING_2D, FLOOR_PLAN_EDITING_3D, FLOOR_PLAN_RENDERING_3D,
 * and MOBILE_APPLICATION_FLOOR_PLAN product kinds.
 */
export const FloorPlanDetailsRenderer: FC<AssignmentRendererProps> = ({ assignment, order, mainProductId }) => {
  const { t } = useTranslation(['gallery'])
  const workspaceId = useAssignmentDetailDrawerWorkspaceId()
  const { data, isLoading, isError, error, refetch } = useGetAssignmentFloorPlanDetails(assignment.id, workspaceId)

  useEffect(() => {
    if (!isError || isNotFoundError(error)) return
    logger.error('FloorPlanDetailsRenderer: failed to load details', error instanceof Error ? error : undefined, {
      context: { component: 'FloorPlanDetailsRenderer', metadata: { assignmentId: assignment.id, workspaceId } },
    })
  }, [isError, error, assignment.id, workspaceId])

  return (
    <RendererQueryState assignmentId={assignment.id} isLoading={isLoading} isError={isError} onRetry={refetch}>

      {!!data &&
        <FloorPlanConfigSection config={data} />
      }

      <DetailSection title={t('gallery:assignment_details.floor_plan.output_language')}>
        <DetailField
          value={data?.outputFileLanguage ? t(`language:${data?.outputFileLanguage.toUpperCase()}`, { defaultValue: data?.outputFileLanguage }) : undefined}
        />
      </DetailSection>

      {!!data &&
        <DetailSection title={t('gallery:assignment_details.floor_plan.product_config')}>
          <DetailBooleanField
            label={t('gallery:assignment_details.floor_plan.draw_specific_unit')}
            value={data.drawOnlySpecificUnit}
          />
          {!!data.drawOnlySpecificUnitComment &&
            <DetailField label={t(data.drawOnlySpecificUnit ? 'gallery:assignment_details.floor_plan.draw_specific_unit_comment' : 'gallery:assignment_details.floor_plan.entire_plan_comment')}>
              <DetailCommentBlock>{data.drawOnlySpecificUnitComment}</DetailCommentBlock>
            </DetailField>
          }
        </DetailSection>
      }

      <DetailSection title={t('gallery:assignment_details.floor_plan.comments')}>
        <DetailCommentBlock>{(trimToNull(data?.clientComment) ?? '-')}</DetailCommentBlock>
      </DetailSection>

      <AssignmentAddonsSection assignment={assignment} order={order} mainProductId={mainProductId} />

      <UploadedVisualsSection assignmentId={assignment.id} />

    </RendererQueryState>
  )
}
